import { useCallback, useMemo, useState } from 'react';
import config from 'config';
import _omit from 'lodash/omit';
import _reduce from 'lodash/reduce';

import { useTranslation } from '@lyearn/i18n';
import { UploadedFile, UppyFileMeta } from '@lyearn/shared/hooks/useUppy/types';
import useUppy from '@lyearn/shared/hooks/useUppy/useUppy';
import { useSnackbar } from '@lyearn/ui';
import { useUppyLazyGetInstance } from '@/components/UppyProvider';
import { getFileCategory } from '@/features/media/utils';
import { PostFragment } from '@/features/messages/graphql/fragments/generated/Post';
import currentUser from '@/helper/currentUser';
import { request } from '@/helper/request';
import { useAppConfig } from '@/hooks/useAppConfig';
import { CreateMediaInput, MediaKindEnum } from '@/types/schema';

import { getDuplicateFileErrorIcon } from '../utils';

type PostAttachment = CreateMediaInput & { name: string; progress?: number; mediaId: string };

const FilesLimitErrorMatcher = 'You can only upload';
const DUPLICATE_FILE_ERROR = 'Cannot add the duplicate file';
const MAX_ALLOWED_FILES = 4;

type FileType = UploadedFile<UppyFileMeta>;

type MediaInputWithProgress = CreateMediaInput & { progress?: number };

export const adaptUploadMedia = (file: FileType) => ({
  mediaId: file.id,
  mimeType: file.meta.type,
  size: file.size,
  name: file.meta.name,
  url: file.url,
  progress: file.fileProgress,
  kind: getFileCategory(file.meta.type!) as unknown as MediaKindEnum,
});

export function adaptMedias(medias?: MediaInputWithProgress[] | null): CreateMediaInput[] {
  if (!medias) {
    return [];
  }

  return medias.map(({ mediaId, progress, ...rest }) => ({ ...rest }));
}

const useAttachmentUpload = ({
  attachments,
  maxAllowedFiles,
}: {
  attachments: PostFragment['attachments'];
  maxAllowedFiles?: number;
}) => {
  const { getUppyInstance } = useUppyLazyGetInstance();
  const resolvedMaxAllowedFiles = maxAllowedFiles ?? MAX_ALLOWED_FILES;
  const { showErrorSnackbar, showSnackbar } = useSnackbar();
  const { t } = useTranslation(['Common']);
  const { site: siteConfig } = useAppConfig();
  const [uploads, setUploads] = useState<{ [key: string]: PostAttachment }>(() =>
    _reduce(
      attachments,
      (acc, attachment) => {
        acc[attachment.media.mediaId] = attachment.media;

        return acc;
      },
      {} as { [key: string]: PostAttachment },
    ),
  );

  const numberOfFiles = Math.max(resolvedMaxAllowedFiles - (attachments?.length ?? 0), 0);
  const uppy = useMemo(
    () =>
      getUppyInstance
        ? getUppyInstance(
            config.uppy_companion_url,
            {
              disabledMultipartUpload: siteConfig.disabledMultipartUpload,
              getAccessToken: currentUser._aT,
              restrictions: { maxNumberOfFiles: numberOfFiles },
            },
            request,
          )
        : null,
    [numberOfFiles, siteConfig.disabledMultipartUpload, getUppyInstance],
  );
  const { files, removeFile: removeUppyFile } = useUppy<FileType>(
    {
      onProgress: (file) => {
        setUploads((uploads) => ({
          ...uploads,
          [file.id]: {
            ...adaptUploadMedia(file),
          },
        }));
      },
      onUploadSuccess: async (file) => {
        setUploads((uploads) => ({
          ...uploads,
          [file.id]: { ...adaptUploadMedia(file) },
        }));

        return file as unknown as UploadedFile;
      },
      onRestrictedFile: (file, error) => {
        if (error.message?.indexOf(FilesLimitErrorMatcher) !== -1) {
          showErrorSnackbar({
            message: t('Common:Error.MaxFiles', { count: resolvedMaxAllowedFiles }),
          });

          return;
        }

        if (error.message?.indexOf(DUPLICATE_FILE_ERROR) !== -1) {
          showSnackbar({
            message: t('Common:Error.DuplicateFile'),
            startIcon: getDuplicateFileErrorIcon(),
          });

          return;
        }

        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      },
      referenceId: 'attachments',
    },
    uppy,
  );

  const removeFile = useCallback(
    (file: any) => {
      const id = file?.mediaId;
      setUploads((uploads) => _omit(uploads, id));
      if (files[id]) {
        removeUppyFile(files[id]);
      }
    },
    [files, removeUppyFile],
  );

  const reset = useCallback(() => {
    uppy?.cancelAll();
    setUploads({});
  }, [uppy]);

  return { files: uploads, removeFile, uppy, reset };
};

export default useAttachmentUpload;
